import { ref, computed, watch, onMounted, nextTick, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { findActiveEntryId } from './useLogs';
import type { TimestampedEntry } from '@/modules/viewer/types/logs';

/**
 * Shared behavior for the log list sub-tabs (Console / Network / Errors).
 *
 * Computes the id of the entry that's "currently active" based on the video's
 * current playback time — defined as the nearest-past entry. When auto-scroll
 * is enabled, scrolls that entry into view as playback progresses.
 *
 * The caller binds `containerRef` to the scrollable list's root element, and
 * `activeEntryId` to each row's `:active` prop + `data-log-id` attribute.
 *
 * @example
 * const { containerRef, activeEntryId } = useActiveLogEntry(entries, autoScroll);
 */
export const useActiveLogEntry = <T extends TimestampedEntry>(entries: Ref<T[]>, autoScroll: Ref<boolean>) => {
  const containerRef = ref<HTMLElement | null>(null);
  const { currentPlaybackTime } = storeToRefs(useViewerItemStore());

  const activeEntryId = computed(() => findActiveEntryId(entries.value, currentPlaybackTime.value));

  const scrollToActive = async () => {
    if (!autoScroll.value || !activeEntryId.value || !containerRef.value) {
      return;
    }
    await nextTick();
    const row = containerRef.value?.querySelector<HTMLElement>(`[data-log-id="${activeEntryId.value}"]`);
    if (row) {
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  watch(activeEntryId, () => scrollToActive());

  onMounted(() => {
    // Ensure the active entry is visible from the start (e.g., when the user
    // switches into Logs mode mid-playback).
    scrollToActive();
  });

  return {
    containerRef,
    activeEntryId,
  };
};
